import { ThunkDispatch, UnknownAction } from '@reduxjs/toolkit';
import { enqueueSnackbar } from 'notistack';

import { translateEditor } from '@/shared/i18n';
import { getIsUserLeftProjectPage } from '@/shared/lib/location';
import { TServerSegment, TUpdatingSegmentData } from '@/shared/types/Editor';
import { Project } from '@/shared/types/Project';

import { RootState } from '../../index';
import {
  markTranscriptVersionAutoSaveDirty,
  setIsVoiceSuggesting,
  updSegment,
  updSegmentFromServer,
} from '../EditorSlice';
import { getRevertedSegmentData, getUnsupersededChangedKeys } from '../utils/getRevertedSegmentData';

type EditorThunkDispatch = ThunkDispatch<RootState, unknown, UnknownAction>;

// Applies a successful segment-save result: syncs each returned segment into the server map and
// marks the transcript dirty for the next version auto-save. No-op if the user already left the
// project page. Shared by the single- (updateSegment) and batch (updateSegments) save thunks.
export const applySavedSegments = (
  projectId: Project['id'],
  updatedSegments: TServerSegment[],
  dispatch: EditorThunkDispatch,
): void => {
  if (getIsUserLeftProjectPage(projectId)) {
    return;
  }

  updatedSegments.forEach((segment) => {
    dispatch(updSegmentFromServer(segment));
  });
  dispatch(markTranscriptVersionAutoSaveDirty());
};

// Handles a failed segment save: for each segment reverts only the fields that don't have a newer
// queued auto-save, flags it with isSavingError, and shows a single snackbar. Shared by both save
// thunks — the single-segment thunk passes a one-element array.
export const revertFailedSegments = (
  changedData: TUpdatingSegmentData[],
  { dispatch, getState }: { dispatch: EditorThunkDispatch; getState: () => RootState },
): void => {
  const {
    editor: { segmentsDataToSave, segmentsFromServerMap },
  } = getState();

  const failedSegments = changedData
    .map((segment) => ({
      segment,
      unsupersededChangedKeys: getUnsupersededChangedKeys(segment, segmentsDataToSave[segment.id]),
    }))
    .filter(({ unsupersededChangedKeys }) => unsupersededChangedKeys.length > 0);

  if (!failedSegments.length) {
    return;
  }

  enqueueSnackbar(translateEditor('editor.segment.saveError'), {
    autoHideDuration: 6000,
  });

  failedSegments.forEach(({ segment, unsupersededChangedKeys }) => {
    const segmentFromServer = segmentsFromServerMap[segment.id];
    const revertedSegmentData = getRevertedSegmentData(segment.id, unsupersededChangedKeys, segmentFromServer);

    // Add isSavingError to the segment to highlight it
    dispatch(updSegment({ id: segment.id, isSavingError: true }));

    // Revert only failed fields that do not have a newer queued save.
    if (revertedSegmentData) {
      dispatch(updSegment(revertedSegmentData));
    }

    if (unsupersededChangedKeys.includes('speaker')) {
      dispatch(setIsVoiceSuggesting(false));
    }
  });
};
